/**
 * 31. Period Close — close checklist, lock/unlock period, dual
 * authorization, close outputs, blocking explanations.
 *
 * Arabic and English (C7): every word from the catalogs; the checks and their
 * blocking reasons, which the API words in English, are shown in the
 * interface's language (./close-text.ts); the period's dates are calendar
 * days (lesson 4), written by the locale. The range is the catalog's: an
 * arrow in English, «من … إلى …» in Arabic, where "→" would point backwards.
 */
import {
  Card,
  PageHeader,
  Skeleton,
  Badge,
  BlockedActionExplanation,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiQuery } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { formatMonth } from "@/lib/i18n/format";
import { statusLabel } from "@/lib/i18n/records";
import { formatDate } from "@/lib/money";
import { closeBlockerText, closeCheckText } from "./close-text";

interface PeriodCloseData {
  period: {
    name: string;
    startDate: string;
    endDate: string;
    status: "Open" | "Closing" | "Closed";
  };
  checklist: Array<{
    item: string;
    done: boolean;
    blocked: boolean;
    message?: string;
  }>;
}

/** A period boundary is a calendar day; the API sends it as a UTC midnight. */
function day(value: string): string {
  return /^\d{4}-\d{2}-\d{2}T00:00:00(\.0+)?Z$/.test(value)
    ? value.slice(0, 10)
    : value;
}

export function PeriodClosePage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiQuery<PeriodCloseData>(
    ["finance", "period-close"],
    "/finance/period-close",
  );

  if (isLoading || !data) return <Skeleton rows={10} />;

  const blockers = data.checklist.filter((c) => c.blocked);

  return (
    <div>
      <PageHeader
        title={t("finance.periodClose.title", {
          period: formatMonth(data.period.name),
        })}
        subtitle={t("finance.periodClose.range", {
          start: formatDate(day(data.period.startDate)),
          end: formatDate(day(data.period.endDate)),
        })}
        actions={
          // Closing and reopening go through the one close path, with the
          // integrity checks and a second person (AE-CL, SN-9); the screen
          // for it is AE-W2. Until then the checklist is shown, and the
          // button says what it waits for (UI-HONEST).
          <PendingButton variant="primary" reason="periodClose" slice="AE-W2">
            {data.period.status === "Closed"
              ? t("finance.periodClose.reopen")
              : t("finance.periodClose.close")}
          </PendingButton>
        }
      />

      <Card
        title={t("finance.periodClose.checklist")}
        actions={
          <Badge
            tone={
              data.period.status === "Closed"
                ? "success"
                : data.period.status === "Closing"
                  ? "primary"
                  : "warning"
            }
          >
            {statusLabel(t, "AccountingPeriod", data.period.status)}
          </Badge>
        }
      >
        {blockers.length > 0 && (
          <BlockedActionExplanation
            title={t("finance.periodClose.blockedTitle")}
            reasons={blockers.map((c) => ({
              code: "PERIOD_CLOSE_BLOCKED",
              message: c.message
                ? closeBlockerText(t, c.message)
                : closeCheckText(t, c.item),
              resolutionPath: t("finance.periodClose.resolve", {
                item: closeCheckText(t, c.item),
              }),
            }))}
          />
        )}
        <ul style={{ margin: 0, paddingInlineStart: 18 }}>
          {data.checklist.map((item) => (
            <li key={item.item} style={{ marginBlockEnd: 8 }}>
              {item.done ? (
                <Badge tone="success">✓</Badge>
              ) : (
                <Badge tone={item.blocked ? "danger" : "warning"}>
                  {item.blocked ? "✗" : "○"}
                </Badge>
              )}{" "}
              {closeCheckText(t, item.item)}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
